<template>
  <v-dialog v-model="dialogVisible" max-width="500" persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-bookmark-multiple</v-icon>
        ブックマークページ管理
        <v-spacer></v-spacer>
        <v-btn icon variant="text" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-4">
        <!-- ページ一覧 -->
        <v-list v-if="pages.length > 0" class="pa-0">
          <v-list-item
            v-for="page in sortedPages"
            :key="page.id"
            :class="{ 'current-page': page.id === currentPageId }"
            @click="switchPage(page)"
            class="page-item mb-2"
          >
            <template v-slot:prepend>
              <v-icon :color="page.id === currentPageId ? 'primary' : 'grey'">
                {{ page.id === currentPageId ? 'mdi-bookmark-check' : 'mdi-bookmark-outline' }}
              </v-icon>
            </template>

            <v-list-item-title class="font-weight-medium">
              {{ page.name || '(名前未設定)' }}
            </v-list-item-title>
            <v-list-item-subtitle class="text-caption">
              {{ page.id === currentPageId ? '現在のページ' : formatLastAccessed(page.lastAccessed) }}
            </v-list-item-subtitle>

            <template v-slot:append>
              <v-btn
                icon
                variant="text"
                size="small"
                @click.stop="startRename(page)"
                class="mr-1"
              >
                <v-icon size="small">mdi-pencil</v-icon>
              </v-btn>
            </template>
          </v-list-item>
        </v-list>

        <div v-else class="text-center text-grey py-4">
          保存されたブックマークページはありません
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-btn variant="text" @click="close">閉じる</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="elevated" prepend-icon="mdi-plus" @click="$emit('create-new-page')">
          新しいページを作成
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- ページ名変更ダイアログ -->
  <v-dialog v-model="renameDialogVisible" max-width="400">
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-pencil</v-icon>
        ページ名を変更
      </v-card-title>
      <v-card-text>
        <v-text-field
          v-model="renameValue"
          label="ページ名"
          :counter="20"
          :maxlength="20"
          :rules="[v => !v || v.length <= 20 || '20文字以内で入力してください']"
          variant="outlined"
          autofocus
          @keydown.enter="handleRenameEnter"
        ></v-text-field>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="renameDialogVisible = false">キャンセル</v-btn>
        <v-btn color="primary" variant="elevated" @click="confirmRename" :disabled="renameValue.length > 20">保存</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'BookmarkPagesModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    pages: {
      type: Array,
      default: () => []
    },
    currentPageId: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'switch-page', 'rename-page', 'create-new-page'],
  data() {
    return {
      renameDialogVisible: false,
      renameValue: '',
      renamingPage: null
    }
  },
  computed: {
    dialogVisible: {
      get() { return this.isOpen; },
      set(val) { if (!val) this.$emit('close'); }
    },
    sortedPages() {
      return [...this.pages].sort((a, b) => {
        // 現在のページを最上部に
        if (a.id === this.currentPageId) return -1;
        if (b.id === this.currentPageId) return 1;
        // 残りは最終アクセス日時の降順
        return new Date(b.lastAccessed || 0) - new Date(a.lastAccessed || 0);
      });
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    switchPage(page) {
      if (page.id !== this.currentPageId) {
        this.$emit('switch-page', page);
      }
    },
    startRename(page) {
      this.renamingPage = page;
      this.renameValue = page.name || '';
      this.renameDialogVisible = true;
    },
    handleRenameEnter(e) {
      if (e.isComposing) return;
      this.confirmRename();
    },
    confirmRename() {
      if (this.renamingPage && this.renameValue.length <= 20) {
        this.$emit('rename-page', { id: this.renamingPage.id, name: this.renameValue });
        this.renameDialogVisible = false;
        this.renamingPage = null;
        this.renameValue = '';
      }
    },
    formatLastAccessed(dateStr) {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return '今日アクセス';
        if (diffDays === 1) return '昨日アクセス';
        if (diffDays < 30) return `${diffDays}日前にアクセス`;
        return date.toLocaleDateString('ja-JP');
      } catch {
        return '';
      }
    }
  }
}
</script>

<style scoped>
.current-page {
  background-color: rgba(233, 208, 202, 0.15) !important;
  border-left: 3px solid #e9d0ca;
}

.page-item {
  border-radius: 8px;
  border: 1px solid #eee;
}
</style>
